import { take } from 'rxjs/operators';
import { BreakpointMinService } from 'src/app/@utils/breakpoint-min.service';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { customFilterParams } from '@features/product-filter/product-filter.config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { SearchStateService } from '../search-state.service';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';

@UntilDestroy()
@Component({
    selector: 'app-search-input',
    templateUrl: './search-input.component.html',
    styleUrls: ['./search-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SvgIconComponent,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        LoadingDotsComponent,
        AsyncPipe,
        TranslateSafePipe
    ]
})
export class SearchInputComponent implements OnInit, AfterViewInit, OnDestroy {
    private eventHandler: () => void;

    public customFilterParams = customFilterParams;
    public searchInput: UntypedFormControl;
    public active = false;
    public hasChanged: boolean = false;

    @ViewChild('wrapper')
    public wrapper: ElementRef<HTMLElement>;

    @ViewChild('expander')
    public expander: ElementRef<HTMLElement>;

    @Output()
    public searchValue = new EventEmitter();

    constructor(
        private readonly ngZone: NgZone,
        private readonly renderer: Renderer2,
        private readonly siteStateService: SiteStateService,
        public readonly searchStateService: SearchStateService,
        public readonly breakpointMinService: BreakpointMinService
    ) {
        this.eventHandler = this.handleTransitionEnd.bind(this);
        // initialize with searchTerm value from searchStateService, for dynamic rendering e.g. breakpoints
        this.searchStateService.searchTerm$
            .pipe(take(1), untilDestroyed(this))
            .subscribe(value => (this.searchInput = new UntypedFormControl(value)));
    }

    ngOnInit(): void {
        this.searchInput.valueChanges.pipe(untilDestroyed(this)).subscribe(searchTerm => {
            if (searchTerm.length) {
                this.hasChanged = true;
                this.expand();
            } else {
                this.shrink();
            }
            this.searchValue.emit(searchTerm);
        });
    }

    ngAfterViewInit(): void {
        const { nativeElement: element } = this.expander;
        this.ngZone.runOutsideAngular(() => {
            element.addEventListener('transitionend', this.eventHandler);
        });

        this.siteStateService.state$.pipe(untilDestroyed(this)).subscribe(({ search }) => {
            if (!search) {
                this.hasChanged = false;
                this.shrink();
            }
        });
    }

    ngOnDestroy(): void {
        this.expander.nativeElement.removeEventListener('transitionend', this.eventHandler);
    }

    public handleFocus(): void {
        if (this.searchInput.value.length && !this.active) {
            this.expand();
            this.searchValue.emit(this.searchInput.value);
        }
    }

    public handleFocusMobile(): void {
        if (this.searchInput.value.length && !this.active) {
            this.searchValue.emit(this.searchInput.value);
        }
        this.active = true;
    }

    public handleBlur(): void {
        if (this.searchInput.value.length && this.hasChanged) {
            return;
        }
        this.hasChanged = false;
        this.shrink();
    }

    public handleBlurMobile(): void {
        if (this.searchInput.value.length) {
            return;
        }
        this.active = false;
    }

    private expand(): void {
        this.active = true;
        this.ngZone.runOutsideAngular(() => {
            const wrapper = this.wrapper.nativeElement;
            const expander = this.expander.nativeElement;
            this.renderer.setStyle(expander, 'position', 'absolute');
            this.renderer.setStyle(expander, 'maxWidth', `${wrapper.offsetWidth}px`);

            requestAnimationFrame(() => {
                this.renderer.setStyle(expander, 'maxWidth', '100%');
            });
        });
    }

    private shrink(): void {
        this.active = false;
        this.ngZone.runOutsideAngular(() => {
            const wrapper = this.wrapper.nativeElement;
            const expander = this.expander.nativeElement;
            this.renderer.setStyle(expander, 'maxWidth', `${wrapper.offsetWidth}px`);
        });
    }

    private handleTransitionEnd(): void {
        if (!this.active) {
            const expand = this.expander.nativeElement;
            this.renderer.setStyle(expand, 'maxWidth', '');
            this.renderer.setStyle(expand, 'position', 'relative');
        }
    }
}
